import type { IMetaColumn, IMetaComputed, IMetaData } from "../types/scad";

export default async function useMetaAsync(
  table: string,
  schema: string = "public",
  immediate: boolean = true,
): Promise<MetaComposable> {
  const router = useRouter();
  const toast = useToast();
  const dataOverride = ref<IMetaData | null>(null);

  const dataResponse = ref<IMetaData | null>(null);
  const pending = ref(false);
  const error = ref(null);

  async function execute() {
    pending.value = true;
    try {
      const response = await $fetch<IMetaData>(
        `/scad/meta/${table}/${schema}?cacheResults=${!import.meta.dev}`,
      );
      dataResponse.value = response;
    } catch (err) {
      toast.add({
        severity: "error",
        summary: "System configuration error!",
        detail: "Please contact an administrator!",
        life: 5000,
      });
    } finally {
      pending.value = false;
    }
  }

  async function refresh() {
    await execute();
  }

  if (immediate) {
    // onMounted(async () => {
      await execute();
    // });
  }
  function configureMetaData(data: IMetaData): IMetaComputed {
    const enabledGridColumns = data.Columns.filter((col) => {
      return col.ShowInGrid;
    }).sort((a, b) => {
      if (a.GridOrder > b.GridOrder) {
        return 1;
      }
      if (a.GridOrder < b.GridOrder) {
        return -1;
      }
      return 0;
    });
    const enabledGridColumnsMap = Object.assign(
      {},
      ...enabledGridColumns.map((col) => ({ [col.ColumnNamePc]: col })),
    );
    // Get enabled edit columns
    const enabledEditFormColumns = data.Columns.filter((col) => {
      return col.ShowInEditForm;
    }).sort((a, b) => {
      if (a.EditFormOrder > b.EditFormOrder) {
        return 1;
      }
      if (a.EditFormOrder < b.EditFormOrder) {
        return -1;
      }
      return 0;
    });
    const enabledEditFormColumnsMap = Object.assign(
      {},
      ...enabledEditFormColumns.map((col) => ({ [col.ColumnNamePc]: col })),
    );
    // Get enabled filter columns
    const enabledFilterColumns = data.Columns.filter((col) => {
      return col.ShowInFilter;
    }).sort((a, b) => {
      if (a.FilterOrder > b.FilterOrder) {
        return 1;
      }
      if (a.FilterOrder < b.FilterOrder) {
        return -1;
      }
      return 0;
    });
    const enabledFilterColumnsMap = Object.assign(
      {},
      ...enabledFilterColumns.map((col) => ({ [col.ColumnNamePc]: col })),
    );
    // Get enabled card columns
    const enabledCardColumns = data.Columns.filter((col) => {
      return col.ShowInCard;
    }).sort((a, b) => {
      if (!a.CardOrder || !b.CardOrder) {
        return 0;
      }
      if (a.CardOrder > b.CardOrder) {
        return 1;
      }
      if (a.CardOrder < b.CardOrder) {
        return -1;
      }
      return 0;
    });
    const enabledCardColumnsMap = Object.assign(
      {},
      ...enabledCardColumns.map((col) => ({ [col.ColumnNamePc]: col })),
    );
    // Get enabled create columns
    const enabledCreateFormColumns = data.Columns.filter((col) => {
      return col.ShowInCreateForm;
    }).sort((a, b) => {
      if (a.CreateFormOrder > b.CreateFormOrder) {
        return 1;
      }
      if (a.CreateFormOrder < b.CreateFormOrder) {
        return -1;
      }
      return 0;
    });
    const enabledCreateFormColumnsMap = Object.assign(
      {},
      ...enabledCreateFormColumns.map((col) => ({ [col.ColumnNamePc]: col })),
    );
    // Get all columns
    const columns = data.Columns;
    const columnsMap = Object.assign(
      {},
      ...columns.map((col) => ({ [col.ColumnNamePc]: col })),
    );
    // Get enabled drilldowns
    const enabledDrilldowns = data.Drilldowns.filter((col) => {
      return col.EditableInForeignKeyParent;
    }).sort((a, b) => {
      if (a.Sequence > b.Sequence) {
        return 1;
      }
      if (a.Sequence < b.Sequence) {
        return -1;
      }
      return 0;
    });
    const enabledDrilldownsMap = Object.assign(
      {},
      ...enabledDrilldowns.map((drilldown) => ({
        [drilldown.ConstraintName]: drilldown,
      })),
    );
    // Get meta column, if it exists...
    const fieldGroups: string[] = [];
    if (data) {
      enabledEditFormColumns.forEach((col) => {
        if (!fieldGroups.includes(col.FieldGroup ?? "")) {
          fieldGroups.push(col.FieldGroup ?? "");
        }
      });
      enabledCreateFormColumns.forEach((col) => {
        if (!fieldGroups.includes(col.FieldGroup ?? "")) {
          fieldGroups.push(col.FieldGroup ?? "");
        }
      });
    }
    // Primary key is the first entry in data.PKeys, otherwise we assume it's an identity column, otherwise Id
    const primaryKeyColumnNamePc = data.PKeys && data.PKeys.length > 0
      ? data.PKeys[0].ColumnNamePc
      : data.Columns.find((col) => col.IsIdentity)?.ColumnNamePc ?? "Id";

    return {
      data: data,
      table: data.Table,
      enabledGridColumns,
      enabledGridColumnsMap,
      enabledEditFormColumns,
      enabledEditFormColumnsMap,
      fieldGroups,
      enabledFilterColumns,
      enabledFilterColumnsMap,
      enabledCardColumns,
      enabledCardColumnsMap,
      enabledCreateFormColumns,
      enabledCreateFormColumnsMap,
      enabledDrilldowns,
      enabledDrilldownsMap,
      columns,
      columnsMap,
      primaryKeyColumnNamePc,
    };
  }
  const data = computed<IMetaComputed | null>(() => {
    if (dataOverride.value) {
      return configureMetaData(dataOverride.value);
    }
    return dataResponse.value
      ? configureMetaData(dataResponse.value as IMetaData)
      : null;
  });
  function getMetaColumn(ColumnNamePc: string): IMetaColumn | null | undefined {
    const matchingColumns = data.value?.data.Columns.filter((col) => {
      return col.ColumnNamePc == ColumnNamePc;
    });

    return matchingColumns ? matchingColumns[0] : null;
  }

  return {
    data,
    rawData: dataResponse,
    rawDataOverride: dataOverride,
    pending,
    execute,
    refresh,
    getColumn: getMetaColumn,
  };
}
